import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Users,
  Shuffle,
  CheckCircle2,
  Flag,
  Pencil,
  Trophy,
  Trash2,
  Info,
  Minus,
  Calendar,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { resolveAssetUrl } from "@/lib/assets";
import { cn } from "@/lib/utils";

interface Member {
  userId: string;
  userName: string;
  userImage?: string;
  role: string;
}

type PointsPerRound = 16 | 24 | 32;
type AmericanoStatus = "draft" | "in_progress" | "completed" | "cancelled";
type AmericanoMode = "rotacion_total" | "equipo_fijo";

const modeLabels: Record<AmericanoMode, string> = {
  rotacion_total: "Rotación total",
  equipo_fijo: "Equipo fijo",
};

interface AmericanoSummary {
  id: string;
  leagueId: string;
  name: string;
  description: string | null;
  pointsPerRound: PointsPerRound;
  mode?: AmericanoMode;
  status: AmericanoStatus;
  eventDate: string | null;
  createdAt: string;
  participantsCount: number;
}

interface ListResponse {
  events: AmericanoSummary[];
  isAdmin: boolean;
}

interface Props {
  leagueId: string;
  members: Member[];
  isAdmin: boolean;
}

const statusLabels: Record<AmericanoStatus, string> = {
  draft: "Borrador",
  in_progress: "En curso",
  completed: "Finalizado",
  cancelled: "Cancelado",
};

const statusColors: Record<AmericanoStatus, string> = {
  draft: "bg-muted text-muted-foreground",
  in_progress: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  completed: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const errMsg = (e: any, fallback: string) =>
  e?.message?.replace(/^\d+:\s*/, "") || fallback;

function getInitials(name: string) {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";
}

/** Format fractional points: up to 1 decimal, strip trailing .0 */
export function formatPoints(value: number) {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function formatEventDate(iso: string | null) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

export function LeagueAmericanosSection({ leagueId, members, isAdmin }: Props) {
  const { toast } = useToast();
  const listKey = ["/api/leagues", leagueId, "americanos"];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const emptyForm = {
    name: "",
    description: "",
    eventDate: "",
    pointsPerRound: 24 as PointsPerRound,
    mode: "rotacion_total" as AmericanoMode,
    participantIds: [] as string[],
    pairs: [] as Array<{ player1Id: string; player2Id: string }>,
    pendingPlayerId: null as string | null,
  };
  const [form, setForm] = useState(emptyForm);

  const { data, isLoading, isError } = useQuery<ListResponse>({ queryKey: listKey });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/leagues/${leagueId}/americanos`, {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        eventDate: form.eventDate ? new Date(form.eventDate).toISOString() : undefined,
        pointsPerRound: form.pointsPerRound,
        mode: form.mode,
        ...(form.mode === "equipo_fijo"
          ? { pairs: form.pairs }
          : { participantIds: form.participantIds }),
      });
      return (await res.json()) as { event: AmericanoSummary };
    },
    onSuccess: ({ event }) => {
      queryClient.invalidateQueries({ queryKey: listKey });
      setShowCreate(false);
      setForm(emptyForm);
      setSelectedId(event.id);
      toast({ title: "Americano creado" });
    },
    onError: (e: any) =>
      toast({ title: "Error", description: errMsg(e, "No se pudo crear el americano"), variant: "destructive" }),
  });

  if (selectedId) {
    return (
      <LeagueAmericanoDetail
        leagueId={leagueId}
        eventId={selectedId}
        members={members}
        isAdmin={isAdmin}
        onBack={() => setSelectedId(null)}
        onDeleted={() => setSelectedId(null)}
      />
    );
  }

  const events = data?.events ?? [];
  const admin = data?.isAdmin ?? isAdmin;

  const toggleParticipant = (userId: string) => {
    setForm((f) => {
      if (f.mode === "equipo_fijo") {
        // Pair builder: taps form pairs in order. Tapping a paired player
        // removes the whole pair; tapping the pending player clears it.
        const inPair = f.pairs.find((p) => p.player1Id === userId || p.player2Id === userId);
        if (inPair) return { ...f, pairs: f.pairs.filter((p) => p !== inPair) };
        if (f.pendingPlayerId === userId) return { ...f, pendingPlayerId: null };
        if (f.pendingPlayerId) {
          return {
            ...f,
            pairs: [...f.pairs, { player1Id: f.pendingPlayerId, player2Id: userId }],
            pendingPlayerId: null,
          };
        }
        return { ...f, pendingPlayerId: userId };
      }
      return {
        ...f,
        participantIds: f.participantIds.includes(userId)
          ? f.participantIds.filter((id) => id !== userId)
          : [...f.participantIds, userId],
      };
    });
  };

  const isEquipoFijo = form.mode === "equipo_fijo";
  const pairNumberByUser = new Map<string, number>();
  form.pairs.forEach((p, i) => {
    pairNumberByUser.set(p.player1Id, i + 1);
    pairNumberByUser.set(p.player2Id, i + 1);
  });
  const selectedCount = isEquipoFijo ? form.pairs.length * 2 : form.participantIds.length;
  const canSubmit =
    form.name.trim().length > 0 &&
    (isEquipoFijo ? form.pairs.length >= 2 && !form.pendingPlayerId : selectedCount >= 4);

  return (
    <div className="space-y-4">
      {admin && (
        <div className="flex justify-end">
          <Button size="sm" className="gap-1" onClick={() => setShowCreate(true)} data-testid="button-create-americano">
            <Plus className="h-4 w-4" />
            Nuevo Americano
          </Button>
        </div>
      )}

      {isLoading ? (
        <Card>
          <CardContent className="p-8 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : isError ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-sm text-muted-foreground">No se pudieron cargar los americanos.</p>
          </CardContent>
        </Card>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="font-medium text-foreground mb-1">Aún no hay americanos en esta liga</p>
            <p className="text-sm text-muted-foreground">
              Los americanos son torneos con parejas rotativas donde todos juegan contra todos.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {events.map((e) => {
            const date = formatEventDate(e.eventDate);
            return (
              <Card
                key={e.id}
                className="cursor-pointer hover-elevate"
                onClick={() => setSelectedId(e.id)}
                data-testid={`card-americano-${e.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium truncate" data-testid={`text-americano-name-${e.id}`}>{e.name}</h4>
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {e.participantsCount} jugadores
                        </span>
                        <span>{e.pointsPerRound} pts/ronda</span>
                        <span>{modeLabels[e.mode ?? "rotacion_total"]}</span>
                        {date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {date}
                          </span>
                        )}
                      </p>
                    </div>
                    <Badge className={statusColors[e.status]} data-testid={`badge-status-${e.id}`}>
                      {statusLabels[e.status]}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo americano</DialogTitle>
            <DialogDescription>Crea un evento social de parejas rotativas.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-muted-foreground">
              Los campos marcados con <span className="text-destructive">*</span> son obligatorios.
            </p>
            <div className="space-y-2">
              <Label>
                Nombre <span className="text-destructive">*</span>
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Americano del sábado"
                data-testid="input-americano-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción (opcional)</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Opcional"
                data-testid="input-americano-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha (opcional)</Label>
              <Input
                type="datetime-local"
                value={form.eventDate}
                onChange={(e) => setForm({ ...form, eventDate: e.target.value })}
                data-testid="input-americano-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Modo (opcional)</Label>
              <div className="grid grid-cols-2 gap-2">
                {(["rotacion_total", "equipo_fijo"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() =>
                      setForm((f) => ({ ...f, mode: m, participantIds: [], pairs: [], pendingPlayerId: null }))
                    }
                    className={cn(
                      "rounded-md border p-2 text-sm text-left hover-elevate",
                      form.mode === m ? "border-primary bg-primary/10 font-medium" : "border-input",
                    )}
                    data-testid={`mode-option-${m}`}
                  >
                    <span className="block">{modeLabels[m]}</span>
                    <span className="block text-xs text-muted-foreground font-normal mt-0.5">
                      {m === "rotacion_total"
                        ? "Las parejas cambian en cada ronda"
                        : "Parejas fijas, todos contra todos"}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Puntos por ronda (opcional)</Label>
              <Select
               
                value={String(form.pointsPerRound)}
                onValueChange={(v) => setForm({ ...form, pointsPerRound: Number(v) as PointsPerRound })}
              >
                <SelectTrigger data-testid="select-points-per-round">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[16, 24, 32].map((p) => (
                    <SelectItem key={p} value={String(p)}>{p} puntos</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>
                  {isEquipoFijo ? "Parejas" : "Participantes"}{" "}
                  <span className="text-destructive">*</span>
                </Label>
                <span className="text-xs text-muted-foreground" data-testid="text-selected-count">
                  {isEquipoFijo ? `${form.pairs.length} parejas` : `${selectedCount} seleccionados`}
                </span>
              </div>
              {isEquipoFijo && (
                <p className="text-xs text-muted-foreground">
                  Toca dos jugadores seguidos para formar una pareja. Toca un jugador emparejado para
                  deshacer su pareja.
                </p>
              )}
              <ScrollArea className="h-56 rounded-md border">
                <div className="p-2 space-y-1">
                  {members.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No hay miembros en la liga.</p>
                  ) : (
                    members.map((m) => {
                      const pairNumber = pairNumberByUser.get(m.userId);
                      const isPending = form.pendingPlayerId === m.userId;
                      const checked = isEquipoFijo
                        ? pairNumber != null || isPending
                        : form.participantIds.includes(m.userId);
                      return (
                        <button
                          key={m.userId}
                          type="button"
                          onClick={() => toggleParticipant(m.userId)}
                          className={cn(
                            "flex items-center gap-2 w-full rounded-md p-2 text-left text-sm hover-elevate",
                            checked && "bg-primary/10",
                          )}
                          data-testid={`participant-option-${m.userId}`}
                        >
                          <Avatar className="h-7 w-7">
                            <AvatarImage src={resolveAssetUrl(m.userImage)} />
                            <AvatarFallback className="text-xs">{getInitials(m.userName)}</AvatarFallback>
                          </Avatar>
                          <span className="flex-1 truncate">{m.userName}</span>
                          {isEquipoFijo && pairNumber != null && (
                            <Badge variant="secondary" className="shrink-0 text-xs">
                              Pareja {pairNumber}
                            </Badge>
                          )}
                          {isEquipoFijo && isPending && (
                            <Badge variant="outline" className="shrink-0 text-xs">
                              Elige compañero…
                            </Badge>
                          )}
                          {!isEquipoFijo && (
                            <span
                              className={cn(
                                "flex h-5 w-5 items-center justify-center rounded-full border shrink-0",
                                checked ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40",
                              )}
                            >
                              {checked && <CheckCircle2 className="h-4 w-4" />}
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
              {isEquipoFijo && form.pairs.length > 0 && form.pairs.length < 2 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Se necesitan al menos 2 parejas para crear el americano.
                </p>
              )}
              {!isEquipoFijo && selectedCount > 0 && selectedCount < 4 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Se necesitan al menos 4 jugadores para crear el americano.
                </p>
              )}
              {!isEquipoFijo && selectedCount >= 4 && selectedCount < 8 && (
                <p className="text-xs text-muted-foreground flex items-start gap-1">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  Con pocos jugadores habrá muchos descansos rotativos ("byes") en cada ronda.
                </p>
              )}
            </div>

            <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground flex items-start gap-2">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Por ahora, los Americanos no afectan tu ELO — es un formato social, no compite por ranking.
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!canSubmit || createMutation.isPending}
              data-testid="button-save-americano"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Crear americano
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Detail                                                              */
/* ------------------------------------------------------------------ */

interface PlayerInfo {
  id: string;
  userId: string;
  totalPoints: number;
  name: string;
  image: string | null;
  isMember?: boolean;
}

interface AmericanoMatch {
  id: string;
  roundId: string;
  matchNumber: number;
  pairAPlayer1Id: string;
  pairAPlayer2Id: string;
  pairBPlayer1Id: string;
  pairBPlayer2Id: string;
  pairAGames: number | null;
  pairBGames: number | null;
  status: "pending" | "completed";
}

interface AmericanoRound {
  id: string;
  roundNumber: number;
  restingPlayerIds: string[];
  isComplete: boolean;
  matches: AmericanoMatch[];
}

interface AmericanoEventFull {
  id: string;
  leagueId: string;
  name: string;
  description: string | null;
  pointsPerRound: PointsPerRound;
  mode?: AmericanoMode;
  status: AmericanoStatus;
  eventDate: string | null;
}

interface DetailData {
  event: AmericanoEventFull;
  participants: PlayerInfo[];
  rounds: AmericanoRound[];
  standings: PlayerInfo[];
  isAdmin: boolean;
}

interface PreviewMatch {
  matchNumber: number;
  pairA: [string, string];
  pairB: [string, string];
}
interface PreviewRound {
  roundNumber: number;
  restingPlayerIds: string[];
  matches: PreviewMatch[];
}
interface PreviewResponse {
  seed: string;
  rounds: PreviewRound[];
}

interface DetailProps {
  leagueId: string;
  eventId: string;
  members: Member[];
  isAdmin: boolean;
  onBack: () => void;
  onDeleted: () => void;
}

function LeagueAmericanoDetail({ leagueId, eventId, members, isAdmin, onBack, onDeleted }: DetailProps) {
  const { toast } = useToast();
  const baseKey = ["/api/leagues", leagueId, "americanos", eventId];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: baseKey });

  const { data, isLoading, isError } = useQuery<DetailData>({ queryKey: baseKey });

  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [showFinalize, setShowFinalize] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [resultMatch, setResultMatch] = useState<AmericanoMatch | null>(null);
  const [pairAScore, setPairAScore] = useState(0);

  // The engine works exclusively with userIds, so persisted match pair ids and
  // restingPlayerIds — as well as the schedule preview ids — are all userIds.
  const memberByUser = useMemo(() => {
    const map = new Map<string, Member>();
    members.forEach((m) => map.set(m.userId, m));
    return map;
  }, [members]);

  const playerByUser = useMemo(() => {
    const map = new Map<string, PlayerInfo>();
    (data?.participants ?? []).forEach((p) => map.set(p.userId, p));
    return map;
  }, [data?.participants]);

  const userName = (userId: string) =>
    playerByUser.get(userId)?.name ?? memberByUser.get(userId)?.userName ?? "Jugador";
  const playerName = userName;

  const points = data?.event.pointsPerRound ?? 24;

  const previewMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/leagues/${leagueId}/americanos/${eventId}/schedule/preview`);
      return (await res.json()) as PreviewResponse;
    },
    onSuccess: (p) => setPreview(p),
    onError: (e: any) =>
      toast({ title: "Error", description: errMsg(e, "No se pudo generar el sorteo"), variant: "destructive" }),
  });

  const confirmMutation = useMutation({
    mutationFn: async (seed: string) =>
      apiRequest("POST", `/api/leagues/${leagueId}/americanos/${eventId}/schedule/confirm`, { seed }),
    onSuccess: () => {
      invalidate();
      setPreview(null);
      toast({ title: "Sorteo confirmado", description: "El americano está en curso." });
    },
    onError: (e: any) =>
      toast({ title: "Error", description: errMsg(e, "No se pudo confirmar el sorteo"), variant: "destructive" }),
  });

  const resultMutation = useMutation({
    mutationFn: async ({ matchId, pairAGames, pairBGames }: { matchId: string; pairAGames: number; pairBGames: number }) =>
      apiRequest("POST", `/api/leagues/${leagueId}/americanos/${eventId}/matches/${matchId}/result`, {
        pairAGames,
        pairBGames,
      }),
    onSuccess: () => {
      invalidate();
      setResultMatch(null);
      toast({ title: "Resultado guardado" });
    },
    onError: (e: any) =>
      toast({ title: "Error", description: errMsg(e, "No se pudo guardar el resultado"), variant: "destructive" }),
  });

  const finalizeMutation = useMutation({
    mutationFn: async () =>
      apiRequest("POST", `/api/leagues/${leagueId}/americanos/${eventId}/finalize`),
    onSuccess: () => {
      invalidate();
      setShowFinalize(false);
      toast({ title: "Americano finalizado" });
    },
    onError: (e: any) =>
      toast({ title: "Error", description: errMsg(e, "No se pudo finalizar"), variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => apiRequest("DELETE", `/api/leagues/${leagueId}/americanos/${eventId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/leagues", leagueId, "americanos"] });
      toast({ title: "Americano eliminado" });
      onDeleted();
    },
    onError: (e: any) =>
      toast({ title: "Error", description: errMsg(e, "No se pudo eliminar"), variant: "destructive" }),
  });

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-16">
        {isError ? (
          <p className="text-sm text-muted-foreground">No se pudo cargar el americano.</p>
        ) : (
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        )}
      </div>
    );
  }

  const { event, rounds, standings } = data;
  const admin = data.isAdmin ?? isAdmin;
  const isDraft = event.status === "draft";
  const isInProgress = event.status === "in_progress";
  const isCompleted = event.status === "completed";
  const isCancelled = event.status === "cancelled";
  const editable = admin && isInProgress;

  const openResult = (match: AmericanoMatch) => {
    setResultMatch(match);
    setPairAScore(match.pairAGames ?? Math.floor(points / 2));
  };

  const submitResult = () => {
    if (!resultMatch) return;
    resultMutation.mutate({
      matchId: resultMatch.id,
      pairAGames: pairAScore,
      pairBGames: points - pairAScore,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="button-americano-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold" data-testid="text-americano-detail-name">{event.name}</h2>
            <Badge className={statusColors[event.status]} data-testid="badge-americano-detail-status">
              {statusLabels[event.status]}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {event.pointsPerRound} puntos por ronda · {standings.length} jugadores ·{" "}
            {modeLabels[event.mode ?? "rotacion_total"]}
          </p>
          {event.description && (
            <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {admin && !isCompleted && !isCancelled && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1 text-destructive"
              onClick={() => setShowDelete(true)}
              data-testid="button-delete-americano"
            >
              <Trash2 className="h-4 w-4" />
              Eliminar
            </Button>
          )}
          {editable && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => setShowFinalize(true)}
              data-testid="button-finalize-americano"
            >
              <Flag className="h-4 w-4" />
              Finalizar americano
            </Button>
          )}
        </div>
      </div>

      {/* Standings */}
      <StandingsCard standings={standings} completed={isCompleted} />

      {/* Draft: schedule preview flow */}
      {isDraft && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Shuffle className="h-4 w-4" /> Sorteo del americano
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!preview ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Genera el sorteo para ver todas las rondas con sus parejas y descansos. Podrás
                  regenerarlo hasta que lo confirmes.
                </p>
                {admin ? (
                  <Button
                    className="w-full gap-1"
                    onClick={() => previewMutation.mutate()}
                    disabled={previewMutation.isPending}
                    data-testid="button-generate-schedule"
                  >
                    {previewMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shuffle className="h-4 w-4" />}
                    Generar sorteo
                  </Button>
                ) : (
                  <p className="text-sm text-muted-foreground text-center">
                    El sorteo aún no se ha generado.
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {preview.rounds.map((r) => (
                    <PreviewRoundCard key={r.roundNumber} round={r} userName={userName} />
                  ))}
                </div>
                {admin && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      variant="outline"
                      className="flex-1 gap-1"
                      onClick={() => previewMutation.mutate()}
                      disabled={previewMutation.isPending || confirmMutation.isPending}
                      data-testid="button-regenerate-schedule"
                    >
                      {previewMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shuffle className="h-4 w-4" />}
                      Regenerar sorteo
                    </Button>
                    <Button
                      className="flex-1 gap-1"
                      onClick={() => confirmMutation.mutate(preview.seed)}
                      disabled={confirmMutation.isPending}
                      data-testid="button-confirm-schedule"
                    >
                      {confirmMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Confirmar sorteo
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* In progress / completed: rounds */}
      {(isInProgress || isCompleted) && rounds.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground flex items-start gap-1">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            El que descansa recibe el promedio de puntos de la ronda.
          </p>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {rounds.map((r) => (
              <RoundCard
                key={r.id}
                round={r}
                points={points}
                playerName={playerName}
                editable={editable}
                onResult={openResult}
              />
            ))}
          </div>
        </div>
      )}

      {/* Result dialog */}
      <Dialog open={!!resultMatch} onOpenChange={(o) => !o && setResultMatch(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {resultMatch?.status === "completed" ? "Editar resultado" : "Registrar resultado"}
            </DialogTitle>
            <DialogDescription>
              {resultMatch
                ? `${playerName(resultMatch.pairAPlayer1Id)} / ${playerName(resultMatch.pairAPlayer2Id)}  vs  ${playerName(resultMatch.pairBPlayer1Id)} / ${playerName(resultMatch.pairBPlayer2Id)}`
                : ""}
            </DialogDescription>
          </DialogHeader>
          {resultMatch && (
            <div className="space-y-4 py-2">
              <div className="flex items-center justify-between gap-4">
                <div className="text-center flex-1">
                  <p className="text-xs text-muted-foreground mb-1 truncate">
                    {playerName(resultMatch.pairAPlayer1Id)} / {playerName(resultMatch.pairAPlayer2Id)}
                  </p>
                  <div className="text-4xl font-bold tabular-nums" data-testid="text-pairA-score">{pairAScore}</div>
                  <p className="text-[11px] text-muted-foreground mt-1">Pareja A</p>
                </div>
                <span className="text-2xl text-muted-foreground">-</span>
                <div className="text-center flex-1">
                  <p className="text-xs text-muted-foreground mb-1 truncate">
                    {playerName(resultMatch.pairBPlayer1Id)} / {playerName(resultMatch.pairBPlayer2Id)}
                  </p>
                  <div className="text-4xl font-bold tabular-nums text-muted-foreground" data-testid="text-pairB-score">
                    {points - pairAScore}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">Pareja B</p>
                </div>
              </div>

              <div className="flex items-center justify-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-12 w-12"
                  onClick={() => setPairAScore((s) => Math.max(0, s - 1))}
                  disabled={pairAScore <= 0}
                  data-testid="button-pairA-minus"
                >
                  <Minus className="h-5 w-5" />
                </Button>
                <span className="text-sm text-muted-foreground w-28 text-center">Puntos Pareja A</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-12 w-12"
                  onClick={() => setPairAScore((s) => Math.min(points, s + 1))}
                  disabled={pairAScore >= points}
                  data-testid="button-pairA-plus"
                >
                  <Plus className="h-5 w-5" />
                </Button>
              </div>

              <div className="grid grid-cols-6 gap-1.5">
                {Array.from({ length: points + 1 }, (_, i) => i).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPairAScore(n)}
                    className={cn(
                      "h-9 rounded-md border text-sm tabular-nums hover-elevate",
                      n === pairAScore && "bg-primary text-primary-foreground border-primary",
                    )}
                    data-testid={`button-score-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground text-center">
                Los puntos deben sumar {points}. La Pareja B se completa automáticamente.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResultMatch(null)}>Cancelar</Button>
            <Button onClick={submitResult} disabled={resultMutation.isPending} data-testid="button-save-americano-result">
              {resultMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finalize confirm */}
      <AlertDialog open={showFinalize} onOpenChange={setShowFinalize}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Finalizar americano?</AlertDialogTitle>
            <AlertDialogDescription>
              Se congelará la clasificación y no podrás registrar ni editar más resultados.
              Puedes finalizar aunque no todas las rondas estén completas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => finalizeMutation.mutate()} data-testid="button-confirm-finalize-americano">
              Finalizar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar americano?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate()}
              data-testid="button-confirm-delete-americano"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

function StandingsCard({ standings, completed }: { standings: PlayerInfo[]; completed: boolean }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Trophy className="h-4 w-4" /> Clasificación
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {standings.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-2">Aún no hay jugadores.</p>
        ) : (
          standings.map((p, i) => {
            const rank = i + 1;
            const podium = completed && rank <= 3;
            const podiumColor =
              rank === 1
                ? "border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20"
                : rank === 2
                ? "border-slate-300/60 bg-slate-50/60 dark:bg-slate-900/30"
                : "border-orange-300/60 bg-orange-50/50 dark:bg-orange-950/20";
            return (
              <div
                key={p.id}
                className={cn(
                  "flex items-center gap-3 rounded-md border p-2",
                  podium ? podiumColor : "border-transparent bg-muted/30",
                )}
                data-testid={`standing-row-${p.id}`}
              >
                <span className="w-6 text-center font-semibold tabular-nums">
                  {completed && rank === 1 ? "🥇" : completed && rank === 2 ? "🥈" : completed && rank === 3 ? "🥉" : rank}
                </span>
                <Avatar className="h-8 w-8">
                  <AvatarImage src={resolveAssetUrl(p.image ?? undefined)} />
                  <AvatarFallback className="text-xs">{getInitials(p.name)}</AvatarFallback>
                </Avatar>
                <span className="flex-1 truncate font-medium">{p.name}</span>
                <span className="font-bold tabular-nums" data-testid={`standing-points-${p.id}`}>
                  {formatPoints(p.totalPoints)}
                </span>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

function PreviewRoundCard({
  round,
  userName,
}: {
  round: PreviewRound;
  userName: (id: string) => string;
}) {
  return (
    <div className="rounded-md border p-3 space-y-2" data-testid={`preview-round-${round.roundNumber}`}>
      <p className="text-sm font-semibold">Ronda {round.roundNumber}</p>
      <div className="space-y-1.5">
        {round.matches.map((m) => (
          <div key={m.matchNumber} className="rounded bg-muted/40 p-2 text-xs">
            <div className="truncate">{userName(m.pairA[0])} / {userName(m.pairA[1])}</div>
            <div className="text-muted-foreground text-[10px] my-0.5">vs</div>
            <div className="truncate">{userName(m.pairB[0])} / {userName(m.pairB[1])}</div>
          </div>
        ))}
      </div>
      {round.restingPlayerIds.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          Descansa: {round.restingPlayerIds.map(userName).join(", ")}
        </p>
      )}
    </div>
  );
}

function RoundCard({
  round,
  points,
  playerName,
  editable,
  onResult,
}: {
  round: AmericanoRound;
  points: number;
  playerName: (id: string) => string;
  editable: boolean;
  onResult: (m: AmericanoMatch) => void;
}) {
  return (
    <div className="rounded-md border p-3 space-y-2" data-testid={`round-${round.roundNumber}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">Ronda {round.roundNumber}</p>
        {round.isComplete && (
          <Badge variant="outline" className="text-[10px] gap-1 border-green-400 text-green-700 dark:text-green-400">
            <CheckCircle2 className="h-3 w-3" /> Completa
          </Badge>
        )}
      </div>
      <div className="space-y-1.5">
        {round.matches.map((m) => {
          const completed = m.status === "completed";
          return (
            <div key={m.id} className="rounded bg-muted/40 p-2 text-xs space-y-1" data-testid={`americano-match-${m.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate flex-1">{playerName(m.pairAPlayer1Id)} / {playerName(m.pairAPlayer2Id)}</span>
                {completed && (
                  <span className="font-bold tabular-nums" data-testid={`match-scoreA-${m.id}`}>{m.pairAGames}</span>
                )}
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate flex-1">{playerName(m.pairBPlayer1Id)} / {playerName(m.pairBPlayer2Id)}</span>
                {completed && (
                  <span className="font-bold tabular-nums" data-testid={`match-scoreB-${m.id}`}>{m.pairBGames}</span>
                )}
              </div>
              {editable && (
                <Button
                  size="sm"
                  variant={completed ? "ghost" : "outline"}
                  className="w-full h-7 gap-1 mt-1"
                  onClick={() => onResult(m)}
                  data-testid={`button-americano-result-${m.id}`}
                >
                  {completed ? <><Pencil className="h-3 w-3" /> Editar</> : `Registrar (${points})`}
                </Button>
              )}
            </div>
          );
        })}
      </div>
      {round.restingPlayerIds.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          Descansa: {round.restingPlayerIds.map(playerName).join(", ")}
        </p>
      )}
    </div>
  );
}
